// components/AdditionalQuestionsModal.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiX, FiMail, FiInstagram, FiTwitter, FiAlertCircle } from 'react-icons/fi';
import '../styles/AdditionalQuestionsModal.scss';

// Predefined field definitions (when additional_info_fields contains string IDs)
const PREDEFINED_FIELDS = {
  email:     { label: 'Email Address',      placeholder: 'your.email@example.com',  icon: <FiMail /> },
  instagram: { label: 'Instagram Handle',   placeholder: '@username',                icon: <FiInstagram /> },
  twitter:   { label: 'Twitter/X Handle',   placeholder: '@username',                icon: <FiTwitter /> },
  snapchat:  { label: 'Snapchat Username',  placeholder: 'username',                 icon: null },
  linkedin:  { label: 'LinkedIn Profile',   placeholder: 'linkedin.com/in/username', icon: null },
};

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Normalise a question (string ID or full object) to { id, label, placeholder, icon, isEmail }
const normalise = (q) => {
  if (typeof q === 'string') {
    const def = PREDEFINED_FIELDS[q] || { label: q, placeholder: 'Enter your answer', icon: null };
    return { id: q, label: def.label, placeholder: def.placeholder, icon: def.icon, isEmail: q === 'email' };
  }
  return {
    id: q.id,
    label: q.label,
    placeholder: q.placeholder || 'Enter your answer',
    icon: null,
    isEmail: false,
    required: q.required,
  };
};

const AdditionalQuestionsModal = ({ questions = [], onClose, onSubmit, isSubmitting }) => {
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});

  const normalisedQuestions = questions.map(normalise);

  useEffect(() => {
    const initial = {};
    normalisedQuestions.forEach(q => { initial[q.id] = ''; });
    setResponses(initial);
    setErrors({});
  }, [questions]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const newErrors = {};
    normalisedQuestions.forEach(q => {
      const value = (responses[q.id] || '').trim();
      if (!value) {
        newErrors[q.id] = 'This field is required';
      } else if (q.isEmail && !validateEmail(value)) {
        newErrors[q.id] = 'Please enter a valid email address';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) onSubmit(responses);
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="additional-questions-modal"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-left">
            <h2>Additional Information</h2>
            <p>Please answer these questions to complete your RSVP</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="questions-list">
            {normalisedQuestions.map(q => (
              <div key={q.id} className="question-field">
                <label>
                  {q.icon && <span className="field-icon">{q.icon}</span>}
                  {q.label}
                  <span className="required">*</span>
                </label>
                <input
                  type={q.isEmail ? 'email' : 'text'}
                  className={`text-input ${errors[q.id] ? 'error' : ''}`}
                  placeholder={q.placeholder}
                  value={responses[q.id] || ''}
                  onChange={(e) => {
                    setResponses(prev => ({ ...prev, [q.id]: e.target.value }));
                    if (errors[q.id]) setErrors(prev => ({ ...prev, [q.id]: null }));
                  }}
                  autoCapitalize="none"
                  autoCorrect="off"
                />
                {errors[q.id] && (
                  <span className="error-message">
                    <FiAlertCircle size={14} />
                    {errors[q.id]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button className="submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Complete RSVP'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdditionalQuestionsModal;
